import { useLazyQuery } from '@apollo/client';
import { AppUser, bankersRound, regularRound, UNIT_OF_MEASURE } from '@bechtel/client-common';
import { GET_THREE_WEEK_LOOK_AHEAD_DATA } from '@bechtel/client-gql';
import { Box, Heading, IconSize, Text } from '@bechtel/gds';
import {
  CrewMember,
  CrewMemberWeeklyLookAheadStatsInput,
  MilestoneStats,
  PersonaType,
  PlanningCrewMemberInput,
  WeeklyLookAheadStatsByPersona,
} from '@bechtel/types';
import { t, Trans } from '@lingui/macro';
import { get, map, startCase, toLower } from 'lodash';
import { FC, useEffect, useState } from 'react';
import { AvatarRegularStyled } from '../../styled';
import { CrewTree } from '../crew-tree';
import {
  ExpandCollapseIconStyled,
  ExpandedItemBorderStyled,
  ThreeWLAItemContainerStyled,
  ThreeWLAItemPersonaStyled,
  ThreeWLAItemWeekStatHeaderStyled,
  ThreeWLAItemWeekStatStyled,
  ThreeWLAPlaceholderStyled,
} from './styled';
export interface ThreeWLAItemProps {
  personaType: PersonaType;
  threeWeekLookAheadStat: WeeklyLookAheadStatsByPersona;
  unitOfMeasure: UNIT_OF_MEASURE;
  selectedCrewMember: CrewMember | null;
  selectedGeneralForeman?: CrewMember | null;
  selectedSuperintendent?: CrewMember | null;
  depth: number;
  user: AppUser | null;
}

const getQuantity = (milestoneStats: MilestoneStats, unitOfMeasure: UNIT_OF_MEASURE, isTarget: boolean = false) => {
  let quantity = '';
  switch (unitOfMeasure) {
    case UNIT_OF_MEASURE.LINEAR_FEET:
      quantity = `${regularRound(milestoneStats.quantity.quantity)} ${t`LF`}`;
      break;
    case UNIT_OF_MEASURE.LINEAR_METERS:
      quantity = `${regularRound(milestoneStats.quantity.quantity)} ${t`LM`}`;
      break;
    case UNIT_OF_MEASURE.MILESTONE:
      if (isTarget) quantity = `${milestoneStats.hours} ${t`hrs`}`;
      else quantity = `${milestoneStats.numMilestones} ${t`ms`}`;
      break;
    default:
      quantity = `${bankersRound(milestoneStats.hours)} ${t`hrs`}`;
  }
  return quantity;
};

export const ThreeWLAItem: FC<ThreeWLAItemProps> = ({
  personaType,
  threeWeekLookAheadStat,
  unitOfMeasure,
  selectedCrewMember,
  depth,
  selectedGeneralForeman,
  selectedSuperintendent,
  user,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [getChildCrewBySupervisorCrewId, result] = useLazyQuery<
    { weeklyLookAheadStatsByPersona: WeeklyLookAheadStatsByPersona[] },
    { input: CrewMemberWeeklyLookAheadStatsInput }
  >(GET_THREE_WEEK_LOOK_AHEAD_DATA, { fetchPolicy: 'network-only' });
  const crewMember = get(threeWeekLookAheadStat, 'crewMember');
  const weeklyLookAheadStats = get(threeWeekLookAheadStat, 'weeklyLookAheadStats');
  const isSelected = !selectedCrewMember || !crewMember ? false : selectedCrewMember.id === crewMember.id;

  useEffect(() => {
    if (!crewMember || !selectedCrewMember || !selectedCrewMember.persona) return;
    if (
      selectedCrewMember.persona == PersonaType.FOREMAN &&
      (crewMember.id == selectedGeneralForeman?.id || crewMember.id == selectedSuperintendent?.id)
    ) {
      setExpanded(true);
      return;
    }
    if (selectedCrewMember.persona == PersonaType.GENERAL_FOREMAN && crewMember.id == selectedSuperintendent?.id) {
      setExpanded(true);
      return;
    }
    if (selectedCrewMember.persona == PersonaType.FOREMAN) return;
    if (crewMember.id == selectedCrewMember.id) setExpanded(true);
  }, [selectedCrewMember, selectedSuperintendent, selectedGeneralForeman]);

  useEffect(() => {
    if (expanded) {
      //General Foreeman can only see their crew
      if (
        user &&
        user.crewMember &&
        user.crewMember.persona &&
        user.crewMember.persona === PersonaType.GENERAL_FOREMAN &&
        crewMember.persona === PersonaType.SUPERINTENDENT
      ) {
        getChildCrewBySupervisorCrewId({
          variables: {
            input: {
              numNextWeeks: 2,
              numPreviousWeeks: 1,
              crewMember: {
                persona: PersonaType.GENERAL_FOREMAN,
                crewMemberId: user.crewMember.id,
              },
            },
          },
        });
        return;
      }
      //Others (SuperUser & SuperAdmin & SuperIntendent) can see the full selected superintendent crew
      const superVisorInput: PlanningCrewMemberInput = {
        persona: crewMember.persona === PersonaType.SUPERINTENDENT ? PersonaType.GENERAL_FOREMAN : PersonaType.FOREMAN,
        crewMemberId: crewMember.id,
      };
      getChildCrewBySupervisorCrewId({
        variables: {
          input: {
            numNextWeeks: 2,
            numPreviousWeeks: 1,
            supervisor: { ...superVisorInput },
          },
        },
      });
    }
  }, [expanded, user, getChildCrewBySupervisorCrewId]);

  return (
    <>
      <ThreeWLAItemContainerStyled personaType={personaType} selected={isSelected}>
        <ThreeWLAPlaceholderStyled
          personaType={personaType}
          selected={isSelected}
          data-testid="selection-placeholder"
        ></ThreeWLAPlaceholderStyled>
        <ThreeWLAItemPersonaStyled>
          {personaType && personaType !== PersonaType.FOREMAN ? (
            <>
              <ExpandCollapseIconStyled
                size={IconSize.LARGE}
                icon={!expanded ? 'plus-circle' : 'minus-circle'}
                onClick={!expanded ? () => setExpanded(true) : () => setExpanded(false)}
              ></ExpandCollapseIconStyled>
              {expanded && <ExpandedItemBorderStyled />}
            </>
          ) : (
            <ExpandCollapseIconStyled
              size={IconSize.LARGE}
              icon="minus-circle"
              style={{ visibility: 'hidden' }}
            ></ExpandCollapseIconStyled>
          )}
          <Box>
            <Box pr={{ sm: '300' }}>
              <AvatarRegularStyled
                hasImageUrl={crewMember && crewMember.imageUrl ? true : false}
                src={crewMember && crewMember.imageUrl ? crewMember.imageUrl : undefined}
              />
            </Box>
            <Box flexDirection={{ sm: 'column' }} mt={{ sm: '400' }}>
              <Heading size="400" weight="600" data-testid="crew-name">
                {startCase(toLower(crewMember.firstName))} {startCase(toLower(crewMember.lastName))}
              </Heading>
            </Box>
          </Box>
          <ThreeWLAItemWeekStatHeaderStyled data-testid="crew-stat-headers">
            <Text size="400" weight="600" color="grey.600" style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
              <Trans>Claimed</Trans>
            </Text>
            <Text size="400" weight="600" color="grey.600" style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
              <Trans>Claimed in Plan</Trans>
            </Text>
            <Text size="400" weight="600" color="grey.600" style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
              <Trans>Planned</Trans>
            </Text>
            <Text size="400" weight="600" color="grey.600" style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
              <Trans>Target</Trans>
            </Text>
            <Text size="400" weight="600" color="grey.600" style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
              <Trans>Approved</Trans>
            </Text>
          </ThreeWLAItemWeekStatHeaderStyled>
        </ThreeWLAItemPersonaStyled>
        {map(weeklyLookAheadStats, (weeklyLookAheadStat, index) => {
          return (
            <ThreeWLAItemWeekStatStyled key={weeklyLookAheadStat.id} data-testid={`crew-quantity-${index}`}>
              <Text size="250" color="grey.1000">
                {getQuantity(weeklyLookAheadStat.claimedMilestoneStats, unitOfMeasure)}
              </Text>
              <Text size="250" color="grey.1000">
                {getQuantity(weeklyLookAheadStat.claimedInPlanMilestoneStats, unitOfMeasure)}
              </Text>
              <Text size="250" color="grey.1000">
                {getQuantity(weeklyLookAheadStat.plannedMilestoneStats, unitOfMeasure)}
              </Text>
              <Text size="250" color="grey.1000">
                {getQuantity(weeklyLookAheadStat.target, unitOfMeasure, true)}
              </Text>
              <Text size="250" color="grey.1000">
                {getQuantity(weeklyLookAheadStat.approvedMilestoneStats, unitOfMeasure)}
              </Text>
              <Box alignItems={{ sm: 'baseline' }}>
                {/* TODO: Index is fragile. But safe if BE confirms they always send 3WLA data even when not planned */}
                {index <= 1 ? (
                  <>
                    <Text size="250" weight="600" color="grey.1000">
                      <Trans>CIP:</Trans>&nbsp;
                    </Text>
                    <Text size="600" color="grey.1000">
                      {weeklyLookAheadStat.earningsInPlan}%
                    </Text>
                  </>
                ) : (
                  <>
                    <Text size="250" weight="600" color="grey.1000">
                      <Trans>Planned:</Trans>&nbsp;
                    </Text>
                    <Text size="600" color="grey.1000">
                      {weeklyLookAheadStat.planningInTarget}%
                    </Text>
                  </>
                )}
              </Box>
            </ThreeWLAItemWeekStatStyled>
          );
        })}
      </ThreeWLAItemContainerStyled>
      {expanded && (
        <CrewTree
          result={result}
          unitOfMeasure={unitOfMeasure}
          selectedCrewMember={selectedCrewMember}
          depth={depth + 1}
          selectedSuperintendent={selectedSuperintendent}
          selectedGeneralForeman={selectedGeneralForeman}
          user={user}
        ></CrewTree>
      )}
    </>
  );
};
